/**
 * Clinical Assessments Panel
 * 
 * Unified view of all clinical assessments:
 * - Falls
 * - Wounds
 * - Pain assessments
 * 
 * Can be embedded in patient chart or used standalone
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  AlertTriangle, Activity, Heart, Plus, ChevronRight,
  Loader2, TrendingUp, TrendingDown, Minus, RefreshCw,
  Calendar, Clock, User, FileText
} from "lucide-react";
import api from "@/lib/api";

// Assessment type configurations
const ASSESSMENT_TYPES = {
  falls: {
    name: "Falls",
    icon: AlertTriangle,
    color: "amber",
    badgeClass: "bg-amber-500/20 text-amber-400 border-amber-500/50"
  },
  wounds: {
    name: "Wounds",
    icon: Activity,
    color: "red",
    badgeClass: "bg-red-500/20 text-red-400 border-red-500/50"
  },
  pain: {
    name: "Pain",
    icon: Heart,
    color: "purple",
    badgeClass: "bg-purple-500/20 text-purple-400 border-purple-500/50"
  }
};

export default function ClinicalAssessmentsPanel({ 
  patientId, 
  patientName,
  compact = false,
  onOpenOverlay,
  isDark = true 
}) {
  const [activeTab, setActiveTab] = useState("falls");
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState({
    falls: [],
    wounds: [],
    pain: []
  });
  const [summary, setSummary] = useState({
    falls: { total: 0, recent: 0, trend: "stable" },
    wounds: { total: 0, active: 0, healing: 0 },
    pain: { avgLevel: 0, trend: "stable" }
  });

  // Fetch all assessments
  const fetchAssessments = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    
    try {
      const [fallsRes, woundsRes, painRes] = await Promise.all([
        api.get(`/clinical/fall-events/${patientId}`).catch(() => null),
        api.get(`/clinical/wound-assessments/${patientId}`).catch(() => null),
        api.get(`/clinical/pain-assessments/${patientId}`).catch(() => null)
      ]);

      // Falls
      const fallEvents = fallsRes?.data?.events || getDemoFalls();
      
      // Wounds
      const woundData = woundsRes?.data?.assessments || getDemoWounds();
      
      // Pain
      const painData = painRes?.data?.assessments || getDemoPain();

      setAssessments({
        falls: fallEvents,
        wounds: woundData,
        pain: painData
      });

      // Calculate summary
      setSummary({
        falls: {
          total: fallEvents.length,
          recent: fallEvents.filter(f => isRecent(f.fall_date, 30)).length,
          trend: fallEvents.length > 2 ? "increasing" : "stable"
        },
        wounds: {
          total: woundData.length,
          active: woundData.filter(w => w.status === "active").length,
          healing: woundData.filter(w => w.status === "healing").length
        },
        pain: {
          avgLevel: painData.length > 0 
            ? Math.round(painData.reduce((sum, p) => sum + (p.pain_level || 0), 0) / painData.length)
            : 0,
          trend: "stable"
        }
      });
    } catch (err) {
      console.error("Error fetching assessments:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  // Helper functions
  const isRecent = (dateStr, days) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = (now - date) / (1000 * 60 * 60 * 24);
    return diffDays <= days;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "increasing": return <TrendingUp className="w-4 h-4 text-red-400" />;
      case "decreasing": return <TrendingDown className="w-4 h-4 text-emerald-400" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  // Demo data generators
  const getDemoFalls = () => [
    {
      id: "demo-fall-1",
      fall_date: "2025-01-15",
      fall_time: "14:30",
      location: "bathroom",
      witnessed: true,
      injuries_present: false,
      interventions: ["vitals_monitored", "family_notified"],
      created_at: "2025-01-15T14:45:00Z"
    }
  ];

  const getDemoWounds = () => [
    {
      id: "demo-wound-1",
      wound_type: "Pressure Ulcer",
      location: "Sacrum",
      stage: "Stage II",
      status: "healing",
      size: "2.5 x 1.8 cm",
      last_assessed: "2025-01-18",
      created_at: "2025-01-10T10:00:00Z"
    }
  ];

  const getDemoPain = () => [
    {
      id: "demo-pain-1",
      pain_level: 4,
      location: "Lower back",
      character: "Aching",
      intervention: "Morphine 10mg",
      relief_achieved: true,
      assessed_at: "2025-01-19T08:00:00Z"
    }
  ];

  // Styles based on theme
  const cardBg = isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200";
  const textColor = isDark ? "text-white" : "text-gray-900";
  const mutedText = isDark ? "text-slate-400" : "text-gray-500";

  if (loading) {
    return (
      <Card className={cardBg}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cardBg} data-testid="clinical-assessments-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`text-lg ${textColor} flex items-center gap-2`}>
              <FileText className="w-5 h-5 text-teal-500" />
              Clinical Assessments
            </CardTitle>
            {patientName && (
              <CardDescription className={mutedText}>
                {patientName}
              </CardDescription>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAssessments} className="border-slate-600">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {/* Falls Summary */}
          <div 
            className={`p-3 rounded-lg cursor-pointer transition-colors ${
              activeTab === "falls" ? "bg-amber-900/30 border border-amber-500/50" : "bg-slate-800/50 hover:bg-slate-700/50"
            }`}
            onClick={() => setActiveTab("falls")}
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className={`text-sm ${mutedText}`}>Falls</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xl font-bold ${textColor}`}>{summary.falls.total}</span>
              <div className="flex items-center gap-1">
                {getTrendIcon(summary.falls.trend)}
                <span className="text-xs text-slate-500">{summary.falls.recent} recent</span>
              </div>
            </div>
          </div>

          {/* Wounds Summary */}
          <div 
            className={`p-3 rounded-lg cursor-pointer transition-colors ${
              activeTab === "wounds" ? "bg-red-900/30 border border-red-500/50" : "bg-slate-800/50 hover:bg-slate-700/50"
            }`}
            onClick={() => setActiveTab("wounds")}
          >
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-red-400" />
              <span className={`text-sm ${mutedText}`}>Wounds</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xl font-bold ${textColor}`}>{summary.wounds.active}</span>
              <span className="text-xs text-emerald-400">{summary.wounds.healing} healing</span>
            </div>
          </div>

          {/* Pain Summary */}
          <div 
            className={`p-3 rounded-lg cursor-pointer transition-colors ${
              activeTab === "pain" ? "bg-purple-900/30 border border-purple-500/50" : "bg-slate-800/50 hover:bg-slate-700/50"
            }`}
            onClick={() => setActiveTab("pain")}
          >
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 text-purple-400" />
              <span className={`text-sm ${mutedText}`}>Pain</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xl font-bold ${textColor}`}>{summary.pain.avgLevel}/10</span>
              {getTrendIcon(summary.pain.trend)}
            </div>
          </div>
        </div>

        {/* Assessment Lists */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 bg-slate-800 mb-3">
            <TabsTrigger value="falls" className="data-[state=active]:bg-amber-600">
              Falls ({assessments.falls.length})
            </TabsTrigger>
            <TabsTrigger value="wounds" className="data-[state=active]:bg-red-600">
              Wounds ({assessments.wounds.length})
            </TabsTrigger>
            <TabsTrigger value="pain" className="data-[state=active]:bg-purple-600">
              Pain ({assessments.pain.length})
            </TabsTrigger>
          </TabsList>

          <ScrollArea className={compact ? "h-[200px]" : "h-[300px]"}>
            {/* Falls Tab */}
            <TabsContent value="falls" className="mt-0 space-y-2">
              {assessments.falls.length === 0 ? (
                <div className="text-center py-4 text-slate-400">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No fall events documented</p>
                </div>
              ) : (
                assessments.falls.map((fall, idx) => (
                  <div key={fall.id || idx} className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className={textColor}>{formatDate(fall.fall_date)}</span>
                        {fall.fall_time && (
                          <span className="text-sm text-slate-500">at {fall.fall_time}</span>
                        )}
                      </div>
                      <Badge variant="outline" className={
                        fall.injuries_present 
                          ? "bg-red-500/20 text-red-400 border-red-500/50"
                          : "bg-emerald-500/20 text-emerald-400 border-emerald-500/50"
                      }>
                        {fall.injuries_present ? "Injury" : "No Injury"}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400">
                      Location: {fall.location} {fall.witnessed ? "(Witnessed)" : "(Unwitnessed)"}
                    </p>
                  </div>
                ))
              )}
              
              <Button 
                variant="outline" 
                className="w-full mt-2 border-amber-500/50 text-amber-400 hover:bg-amber-900/20"
                onClick={() => onOpenOverlay?.("fall_event")}
              >
                <Plus className="w-4 h-4 mr-2" />
                Document Fall Event
              </Button>
            </TabsContent>

            {/* Wounds Tab */}
            <TabsContent value="wounds" className="mt-0 space-y-2">
              {assessments.wounds.length === 0 ? (
                <div className="text-center py-4 text-slate-400">
                  <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No wound assessments</p>
                </div>
              ) : (
                assessments.wounds.map((wound, idx) => (
                  <div key={wound.id || idx} className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium ${textColor}`}>{wound.wound_type}</span>
                      <Badge variant="outline" className={
                        wound.status === "active" 
                          ? "bg-red-500/20 text-red-400 border-red-500/50"
                          : "bg-emerald-500/20 text-emerald-400 border-emerald-500/50"
                      }>
                        {wound.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400">
                      {wound.location} • {wound.stage} • {wound.size}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Last assessed: {formatDate(wound.last_assessed)}
                    </p>
                  </div>
                ))
              )}
              
              <Button 
                variant="outline" 
                className="w-full mt-2 border-red-500/50 text-red-400 hover:bg-red-900/20"
                onClick={() => onOpenOverlay?.("wound_care")}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Wound Assessment
              </Button>
            </TabsContent>

            {/* Pain Tab */}
            <TabsContent value="pain" className="mt-0 space-y-2">
              {assessments.pain.length === 0 ? (
                <div className="text-center py-4 text-slate-400">
                  <Heart className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No pain assessments</p>
                </div>
              ) : (
                assessments.pain.map((pain, idx) => (
                  <div key={pain.id || idx} className="p-3 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          pain.pain_level >= 7 ? "bg-red-500/30 text-red-400" :
                          pain.pain_level >= 4 ? "bg-amber-500/30 text-amber-400" :
                          "bg-emerald-500/30 text-emerald-400"
                        }`}>
                          {pain.pain_level}
                        </div>
                        <span className={textColor}>{pain.location}</span>
                      </div>
                      {pain.relief_achieved && (
                        <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
                          Relief achieved
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-400">
                      {pain.character} • {pain.intervention}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatDate(pain.assessed_at)}
                    </p>
                  </div>
                ))
              )}
              
              <Button 
                variant="outline" 
                className="w-full mt-2 border-purple-500/50 text-purple-400 hover:bg-purple-900/20"
                onClick={() => onOpenOverlay?.("pain_management")}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Pain Assessment
              </Button>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export { ClinicalAssessmentsPanel };
